import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { surface, tone } from '../timeclock/tcFormat';
import { niceDate, period, teamError, teamPost } from './teamFormat';
import { Panel } from './TeamParts';

/**
 * Beheer: sick reports of the last 12 months without a doctor's certificate yet,
 * for whoever sees sickness (management, Els). "Remind" mails the colleague that
 * the certificate is still missing; the date of the last reminder stays visible.
 */
const TeamMissingCertPanel = ({ t, lang, theme, token, notify, reloadKey, year }) => {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');
    const [again, setAgain] = useState(0);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        let alive = true;
        teamPost('missingAttachments', token, { year }).then((response) => {
            if (!alive) return;
            if (response?.success) { setRows(response.data || []); setError(''); } else setError(teamError(t, response));
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, again, reloadKey, year]);

    const remind = (r) => {
        setBusy(r.id);
        teamPost('remindAttachment', token, { id: r.id }).then((response) => {
            setBusy(null);
            if (response?.success) {
                notify(t('team_cert_reminded', { defaultValue: 'Reminder sent to {{name}}.', name: r.staff_name }), true);
                setAgain((k) => k + 1);
            } else {
                notify(teamError(t, response), false);
            }
        });
    };

    const s = surface(theme);
    const cell = { px: 1.25, py: 0.9, borderTop: s.line, fontSize: 13.5, verticalAlign: 'middle' };

    return (
        <Panel theme={theme} title={t('team_cert_title', 'Sick reports without a doctor\'s certificate') + ' · ' + year}>
            {error && <Typography sx={{ color: tone.bad, fontSize: 13.5 }}>{error}</Typography>}
            {rows && rows.length === 0 && (
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>{t('team_cert_none_year', { defaultValue: 'Every sick report of {{year}} has its certificate.', year })}</Typography>
            )}
            {rows && rows.length > 0 && (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id}>
                                    <Box component="td" sx={{ ...cell, fontWeight: 600, whiteSpace: 'nowrap' }}>{r.staff_name}</Box>
                                    <Box component="td" sx={{ ...cell, whiteSpace: 'nowrap' }}>
                                        {period(r.from, r.to, lang)}
                                        {r.days_count ? <Box component="span" sx={{ color: 'text.secondary' }}> · {r.days_count} {r.days_count === 1 ? t('team_day', 'day') : t('team_days', 'days')}</Box> : null}
                                    </Box>
                                    <Box component="td" sx={{ ...cell, color: 'text.secondary', minWidth: 150 }}>
                                        {r.attachment_reminded_at
                                            ? t('team_cert_reminded_on', 'reminded on') + ' ' + niceDate(r.attachment_reminded_at, lang) + (r.reminded_by ? ' (' + r.reminded_by + ')' : '')
                                            : t('team_cert_not_reminded', 'not reminded yet')}
                                    </Box>
                                    <Box component="td" sx={{ ...cell, textAlign: 'right' }}>
                                        <Button size="small" variant="outlined" disabled={busy === r.id || !r.has_email} onClick={() => remind(r)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                                            {r.has_email ? t('team_cert_remind', 'Send reminder') : t('team_cert_no_email', 'no e-mail')}
                                        </Button>
                                    </Box>
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </Box>
            )}
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {t('team_cert_note', 'The colleague gets a mail with a link to My leave, where the certificate can be sent afterwards. Only whether a certificate came in is kept, never the file itself.')}
            </Typography>
        </Panel>
    );
};

export default TeamMissingCertPanel;
